import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  AllTemplatesListFormResponse,
  createTemplate,
  DeviceTemplate,
  getAllTemplatesListForm,
  getAllTemplatesTable,
  getFullTemplate,
  getTemplateDocumentation,
  getTemplateForDevice,
  GetTemplateForDevicePayload,
  getTemplateResetTable,
  ResetTableData,
  TableTemplate,
  TemplateForDeviceResponse,
  TemplateUpsertRequest,
  updateTemplate,
} from '@/api/template.api'
import type { DocumentationFileItem } from '@/features/wizard-page/types'
import { resetStore } from '@/store/actions/reset'
import type { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

type TemplatesState = {
  templatesTableData: Record<string, TableTemplate>
  allTemplatesTableStatus: Status
  templatesFormData: Record<string, AllTemplatesListFormResponse>
  templatesFormDataStatus: Status
  templateDetailsData: Record<string, DeviceTemplate>
  templateDetailsStatusById: Record<string, Status>
  templateDocumentationById: Record<string, DocumentationFileItem[]>
  templateDocumentationStatusById: Record<string, Status>
  templateResetTableById: Record<string, ResetTableData>
  templateResetTableStatusById: Record<string, Status>
  templateForDeviceData: Record<string, TemplateForDeviceResponse>
  templateForDeviceStatus: Status
  createTemplateStatus: Status
  updateTemplateStatus: Status
}

const initialState: TemplatesState = {
  templatesTableData: {},
  allTemplatesTableStatus: 'idle',
  templatesFormData: {},
  templatesFormDataStatus: 'idle',
  templateDetailsData: {},
  templateDetailsStatusById: {},
  templateDocumentationById: {},
  templateDocumentationStatusById: {},
  templateResetTableById: {},
  templateResetTableStatusById: {},
  templateForDeviceData: {},
  templateForDeviceStatus: 'idle',
  createTemplateStatus: 'idle',
  updateTemplateStatus: 'idle',
}

export const getTemplateForDeviceData = createAsyncThunk<TemplateForDeviceResponse, GetTemplateForDevicePayload>(
  'templates/getTemplateForDevice',
  async (payload, { rejectWithValue }) => {
    try {
      return await getTemplateForDevice(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllTemplatesListFormData = createAsyncThunk<AllTemplatesListFormResponse[]>(
  'templates/getAllDeviceTemplates',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllTemplatesListForm()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllTemplatesTableData = createAsyncThunk<TableTemplate[]>(
  'templates/getAllTemplatesTable',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllTemplatesTable()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

/** Rekord odczytany zaraz po zapisie. `null` oznacza, że sam zapis się powiódł, ale
 * doczytanie nie — wtedy slice wymusza ponowne pobranie zamiast trzymać kopię sprzed
 * zapisu. Zapis jest w tym momencie faktem, więc nieudany odczyt nie może odrzucić
 * całego thunka i pokazać błędu operacji, która się udała. */
const refreshedTemplate = async (id: string): Promise<DeviceTemplate | null> => {
  try {
    return await getFullTemplate(id)
  } catch {
    return null
  }
}

export const updateTemplateData = createAsyncThunk<DeviceTemplate | null, TemplateUpsertRequest & { id: string }>(
  'templates/updateTemplateData',
  async (payload, { rejectWithValue }) => {
    try {
      await updateTemplate(payload)
      return await refreshedTemplate(payload.id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createTemplateData = createAsyncThunk<
  { id: string; template: DeviceTemplate | null },
  TemplateUpsertRequest
>('templates/createTemplateData', async (payload, { rejectWithValue }) => {
  try {
    const id = await createTemplate(payload)
    return { id, template: await refreshedTemplate(id) }
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const getFullTemplateData = createAsyncThunk<DeviceTemplate, string>(
  'templates/getFullTemplateData',
  async (id, { rejectWithValue }) => {
    try {
      return await getFullTemplate(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getTemplateDocumentationData = createAsyncThunk<{ id: string; files: DocumentationFileItem[] }, string>(
  'templates/getTemplateDocumentation',
  async (id, { rejectWithValue }) => {
    try {
      return { id, files: await getTemplateDocumentation(id) }
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getTemplateResetTableData = createAsyncThunk<{ id: string; rows: ResetTableData }, string>(
  'templates/getTemplateResetTable',
  async (id, { rejectWithValue }) => {
    try {
      return { id, rows: await getTemplateResetTable(id) }
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const templateSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resetStore, () => initialState)
      .addCase(getAllTemplatesTableData.pending, (state) => {
        state.allTemplatesTableStatus = 'loading'
      })
      .addCase(getAllTemplatesTableData.rejected, (state) => {
        state.allTemplatesTableStatus = 'failed'
      })
      .addCase(getAllTemplatesTableData.fulfilled, (state, action) => {
        state.templatesTableData = Object.fromEntries(action.payload.map((template) => [template.id, template]))
        state.allTemplatesTableStatus = 'succeeded'
      })
      .addCase(getAllTemplatesListFormData.pending, (state) => {
        state.templatesFormDataStatus = 'loading'
      })
      .addCase(getAllTemplatesListFormData.rejected, (state) => {
        state.templatesFormDataStatus = 'failed'
      })
      .addCase(getAllTemplatesListFormData.fulfilled, (state, action) => {
        state.templatesFormData = Object.fromEntries(action.payload.map((template) => [template.id, template]))
        state.templatesFormDataStatus = 'succeeded'
      })
      .addCase(getFullTemplateData.pending, (state, action) => {
        state.templateDetailsStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getFullTemplateData.rejected, (state, action) => {
        state.templateDetailsStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getFullTemplateData.fulfilled, (state, action) => {
        state.templateDetailsData[action.payload.id] = action.payload
        state.templateDetailsStatusById[action.payload.id] = 'succeeded'
      })
      .addCase(getTemplateDocumentationData.pending, (state, action) => {
        state.templateDocumentationStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getTemplateDocumentationData.rejected, (state, action) => {
        state.templateDocumentationStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getTemplateDocumentationData.fulfilled, (state, action) => {
        if (state.templateDocumentationStatusById[action.payload.id] !== 'loading') return
        state.templateDocumentationById[action.payload.id] = action.payload.files
        state.templateDocumentationStatusById[action.payload.id] = 'succeeded'
      })
      .addCase(getTemplateResetTableData.pending, (state, action) => {
        state.templateResetTableStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getTemplateResetTableData.rejected, (state, action) => {
        state.templateResetTableStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getTemplateResetTableData.fulfilled, (state, action) => {
        if (state.templateResetTableStatusById[action.payload.id] !== 'loading') return
        state.templateResetTableById[action.payload.id] = action.payload.rows
        state.templateResetTableStatusById[action.payload.id] = 'succeeded'
      })
      .addCase(getTemplateForDeviceData.pending, (state) => {
        state.templateForDeviceStatus = 'loading'
      })
      .addCase(getTemplateForDeviceData.rejected, (state) => {
        state.templateForDeviceStatus = 'failed'
      })
      .addCase(getTemplateForDeviceData.fulfilled, (state, action) => {
        state.templateForDeviceData[action.payload.id] = action.payload
        state.templateForDeviceStatus = 'succeeded'
      })
      .addCase(createTemplateData.pending, (state) => {
        state.createTemplateStatus = 'loading'
      })
      .addCase(createTemplateData.rejected, (state) => {
        state.createTemplateStatus = 'failed'
      })
      .addCase(createTemplateData.fulfilled, (state, action) => {
        const { id, template: fresh } = action.payload
        const request = action.meta.arg
        const now = new Date()

        if (fresh) {
          state.templateDetailsData[id] = fresh
          state.templateDetailsStatusById[id] = 'succeeded'
        } else {
          delete state.templateDetailsData[id]
          state.templateDetailsStatusById[id] = 'idle'
        }

        state.templatesTableData[id] = {
          id,
          name: fresh?.name ?? request.name,
          language: fresh?.language ?? request.language,
          createdAt: fresh?.createdAt ?? now,
          updatedAt: fresh?.updatedAt ?? now,
          devices: [],
        }
        state.templatesFormData[id] = {
          id,
          name: fresh?.name ?? request.name,
          language: fresh?.language ?? request.language,
        }
        state.createTemplateStatus = 'succeeded'
      })
      .addCase(updateTemplateData.pending, (state) => {
        state.updateTemplateStatus = 'loading'
      })
      .addCase(updateTemplateData.rejected, (state) => {
        state.updateTemplateStatus = 'failed'
      })
      .addCase(updateTemplateData.fulfilled, (state, action) => {
        const request = action.meta.arg
        const fresh = action.payload
        const tableTemplate = state.templatesTableData[request.id]

        if (request.documentationFiles !== undefined) {
          state.templateDocumentationById[request.id] = request.documentationFiles
          state.templateDocumentationStatusById[request.id] = 'succeeded'
        }
        if (request.resetTableData !== undefined) {
          state.templateResetTableById[request.id] = request.resetTableData
          state.templateResetTableStatusById[request.id] = 'succeeded'
        }

        delete state.templateForDeviceData[request.id]

        if (fresh) {
          state.templateDetailsData[request.id] = fresh
          state.templateDetailsStatusById[request.id] = 'succeeded'
        } else {
          delete state.templateDetailsData[request.id]
          state.templateDetailsStatusById[request.id] = 'idle'
        }

        state.templatesTableData[request.id] = {
          id: request.id,
          name: fresh?.name ?? request.name,
          language: fresh?.language ?? request.language,
          createdAt: tableTemplate?.createdAt ?? fresh?.createdAt ?? new Date(),
          updatedAt: fresh?.updatedAt ?? new Date(),
          devices: tableTemplate?.devices ?? [],
        }
        state.templatesFormData[request.id] = {
          id: request.id,
          name: fresh?.name ?? request.name,
          language: fresh?.language ?? request.language,
        }
        state.updateTemplateStatus = 'succeeded'
      })
  },
})

export default templateSlice.reducer
